/**
 * 📋 DRIVER VERIFICATION FORM - Progressive form with auto-save
 * Implements complete driver verification workflow with document upload,
 * progress tracking, and backend submission
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Assignment,
  CloudUpload,
  CheckCircle,
  Warning,
  Info,
  Save,
  Send,
  ArrowBack,
  ArrowForward,
  PhotoCamera,
  Description,
  VerifiedUser,
  Security,
  Person,
  DirectionsCar,
  ContactPhone,
  Close,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { VerificationStatus } from '@/types';
import { RejectionNotice } from '@/components/verification/RejectionNotice';
import { verificationPersistence } from '@/utils/verificationPersistence';

interface VerificationFormData {
  // Personal Information
  personalInfo: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    nin: string; // National Identification Number
    bvn: string; // Bank Verification Number
    address: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
  };

  // Driver's License Information
  driverLicense: {
    licenseNumber: string;
    licenseClass: string;
    issueDate: string;
    expiryDate: string;
    issuingState: string;
    frontImage?: File;
    backImage?: File;
  };

  // Vehicle Information (if applicable)
  vehicleInfo: {
    hasVehicle: boolean;
    make?: string;
    model?: string;
    year?: string;
    plateNumber?: string;
    insurancePolicyNumber?: string;
    insuranceExpiry?: string;
    registrationDocument?: File;
    insuranceDocument?: File;
    vehiclePhotos?: File[];
  };

  // Emergency Contacts / Guarantors
  guarantors: {
    primary: {
      name: string;
      relationship: string;
      phone: string;
      email: string;
      address: string;
    };
    secondary: {
      name: string;
      relationship: string;
      phone: string;
      email: string;
      address: string;
    };
  };

  // Background Check Consent
  backgroundCheck: {
    consentGiven: boolean;
    criminalHistoryDisclosure: string;
    drivingHistoryConsent: boolean;
  };

  // Banking Information
  bankingInfo: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    bankCode: string;
  };
}

interface VerificationProgress {
  completionPercentage: number;
  missingFields: string[];
  completedSections: string[];
  canSubmit: boolean;
}

const initialFormData: VerificationFormData = {
  personalInfo: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    nin: '',
    bvn: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Nigeria'
    }
  },
  driverLicense: {
    licenseNumber: '',
    licenseClass: '',
    issueDate: '',
    expiryDate: '',
    issuingState: ''
  },
  vehicleInfo: {
    hasVehicle: false
  },
  guarantors: {
    primary: {
      name: '',
      relationship: '',
      phone: '',
      email: '',
      address: ''
    },
    secondary: {
      name: '',
      relationship: '',
      phone: '',
      email: '',
      address: ''
    }
  },
  backgroundCheck: {
    consentGiven: false,
    criminalHistoryDisclosure: '',
    drivingHistoryConsent: false
  },
  bankingInfo: {
    accountName: '',
    accountNumber: '',
    bankName: '',
    bankCode: ''
  }
};

const verificationSteps = [
  { label: 'Personal Info', icon: <Person /> },
  { label: 'Driver License', icon: <Description /> },
  { label: 'Vehicle Details', icon: <DirectionsCar /> },
  { label: 'Guarantors', icon: <ContactPhone /> },
  { label: 'Background Check', icon: <Security /> },
  { label: 'Banking Info', icon: <Assignment /> },
  { label: 'Review & Submit', icon: <Send /> }
];

export const DriverVerificationForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<VerificationFormData>(initialFormData);
  const [progress, setProgress] = useState<VerificationProgress>({
    completionPercentage: 0,
    missingFields: [],
    completedSections: [],
    canSubmit: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get verification purpose from navigation state
  const verificationPurpose = location.state?.purpose || 'customer_verification';
  const returnUrl = location.state?.returnUrl || '/dashboard';

  // Auto-save functionality
  const debouncedSave = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (data: VerificationFormData) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(data, true);
        }, 2000); // Save 2 seconds after user stops typing
      };
    })(),
    []
  );

  useEffect(() => {
    // Load existing verification data if available
    loadExistingVerification();
  }, []);

  useEffect(() => {
    // Auto-save when form data changes
    if (Object.keys(formData.personalInfo).some(key => 
      formData.personalInfo[key as keyof typeof formData.personalInfo] !== 
      initialFormData.personalInfo[key as keyof typeof initialFormData.personalInfo]
    )) {
      debouncedSave(formData);
    }
  }, [formData, debouncedSave]);

  const loadExistingVerification = async () => {
    try {
      // Check for rejection info first
      const rejectionInfo = user?.id ? verificationPersistence.getRejectedFieldInfo(user.id) : null;
      
      // In real implementation, load from API
      // const response = await fetch(`/api/v1/driver-verification/user/${user?.id}`);
      // if (response.ok) {
      //   const verification = await response.json();
      //   setFormData(verification.data);
      //   calculateProgress(verification.data);
      // }
      
      // For now, load from localStorage as fallback
      const saved = localStorage.getItem(`verification-${user?.id}`);
      if (saved) {
        const parsedData = JSON.parse(saved);
        
        // If there's rejection info, we might want to preserve some data
        // but clear the rejected fields so users can re-enter them
        if (rejectionInfo && rejectionInfo.rejectedFields.length > 0) {
          // Here you could implement logic to clear specific fields
          // based on what was rejected
        }
        
        setFormData(parsedData);
        calculateProgress(parsedData);
      } else if (rejectionInfo && rejectionInfo.preservedData) {
        // If no saved data but we have preserved data from rejection,
        // load the preserved data (non-rejected fields)
        setFormData(rejectionInfo.preservedData);
        calculateProgress(rejectionInfo.preservedData);
      }
    } catch (error) {
      console.error('Error loading verification data:', error);
    }
  };

  const calculateProgress = (data: VerificationFormData): VerificationProgress => {
    const sections = [
      'personalInfo',
      'driverLicense', 
      'vehicleInfo',
      'guarantors',
      'backgroundCheck',
      'bankingInfo'
    ];

    const completedSections: string[] = [];
    const missingFields: string[] = [];

    // Check personal info completion
    const personalFields = Object.keys(data.personalInfo);
    const personalCompleted = personalFields.every(field => {
      if (field === 'address') {
        return Object.values(data.personalInfo.address).every(val => val.length > 0);
      }
      return data.personalInfo[field as keyof typeof data.personalInfo].length > 0;
    });
    
    if (personalCompleted) {
      completedSections.push('personalInfo');
    } else {
      missingFields.push('Complete personal information');
    }

    // Check driver license completion
    const licenseCompleted = data.driverLicense.licenseNumber && 
                            data.driverLicense.licenseClass &&
                            data.driverLicense.issueDate &&
                            data.driverLicense.expiryDate;
    
    if (licenseCompleted) {
      completedSections.push('driverLicense');
    } else {
      missingFields.push('Complete driver license information');
    }

    // Check vehicle info (optional if no vehicle)
    const vehicleCompleted = !data.vehicleInfo.hasVehicle || 
                            (data.vehicleInfo.make && data.vehicleInfo.model && data.vehicleInfo.plateNumber);
    
    if (vehicleCompleted) {
      completedSections.push('vehicleInfo');
    } else if (data.vehicleInfo.hasVehicle) {
      missingFields.push('Complete vehicle information');
    }

    // Check guarantors
    const guarantorsCompleted = data.guarantors.primary.name &&
                               data.guarantors.primary.phone &&
                               data.guarantors.secondary.name &&
                               data.guarantors.secondary.phone;
    
    if (guarantorsCompleted) {
      completedSections.push('guarantors');
    } else {
      missingFields.push('Provide guarantor information');
    }

    // Check background check consent
    if (data.backgroundCheck.consentGiven && data.backgroundCheck.drivingHistoryConsent) {
      completedSections.push('backgroundCheck');
    } else {
      missingFields.push('Provide background check consent');
    }

    // Check banking info
    if (data.bankingInfo.accountNumber && data.bankingInfo.bankName) {
      completedSections.push('bankingInfo');
    } else {
      missingFields.push('Provide banking information');
    }

    const completionPercentage = Math.round((completedSections.length / sections.length) * 100);
    const canSubmit = completionPercentage === 100;

    const progressData = {
      completionPercentage,
      missingFields,
      completedSections,
      canSubmit
    };

    setProgress(progressData);
    return progressData;
  };

  const saveProgress = async (data: VerificationFormData, isDraft = true) => {
    if (isSaving) return;
    
    setIsSaving(true);
    setError(null);

    try {
      const progressData = calculateProgress(data);
      
      // In real implementation, save to backend
      // const response = await fetch('/api/v1/driver-verification', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     userId: user?.id,
      //     isDraft,
      //     purpose: verificationPurpose,
      //     data,
      //     progress: progressData
      //   })
      // });

      // For now, save to localStorage
      localStorage.setItem(`verification-${user?.id}`, JSON.stringify(data));
      localStorage.setItem(`verification-progress-${user?.id}`, JSON.stringify(progressData));

    } catch (error) {
      console.error('Error saving verification:', error);
      setError('Failed to save progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (section: keyof VerificationFormData, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedFieldChange = (section: keyof VerificationFormData, nestedSection: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [nestedSection]: {
          ...(prev[section] as any)[nestedSection],
          [field]: value
        }
      }
    }));
  };

  const handleFileUpload = (section: keyof VerificationFormData, field: string, files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: file
        }
      }));
    }
  };

  const handleSubmitVerification = async () => {
    if (!progress.canSubmit) {
      setError('Please complete all required fields before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // In real implementation, submit to backend
      // const response = await fetch('/api/v1/driver-verification/submit', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     userId: user?.id,
      //     purpose: verificationPurpose,
      //     data: formData
      //   })
      // });

      // Simulate submission delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Update localStorage with submitted status
      const submissionData = {
        ...formData,
        submittedAt: new Date().toISOString(),
        status: VerificationStatus.PENDING_REVIEW
      };
      localStorage.setItem(`verification-${user?.id}`, JSON.stringify(submissionData));
      localStorage.setItem(`verification-status-${user?.id}`, VerificationStatus.PENDING_REVIEW);

      setSuccessDialog(true);

    } catch (error) {
      console.error('Error submitting verification:', error);
      setError('Failed to submit verification. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStepContent = () => {
    switch (currentStep) {
      case 0: // Personal Info
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Personal Information</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.personalInfo.firstName}
                  onChange={(e) => handleFieldChange('personalInfo', 'firstName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.personalInfo.lastName}
                  onChange={(e) => handleFieldChange('personalInfo', 'lastName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={formData.personalInfo.dateOfBirth}
                  onChange={(e) => handleFieldChange('personalInfo', 'dateOfBirth', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={formData.personalInfo.gender}
                    onChange={(e) => handleFieldChange('personalInfo', 'gender', e.target.value)}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="NIN (National ID Number)"
                  value={formData.personalInfo.nin}
                  onChange={(e) => handleFieldChange('personalInfo', 'nin', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="BVN (Bank Verification Number)"
                  value={formData.personalInfo.bvn}
                  onChange={(e) => handleFieldChange('personalInfo', 'bvn', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Address Information</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.personalInfo.address.street}
                  onChange={(e) => handleNestedFieldChange('personalInfo', 'address', 'street', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.personalInfo.address.city}
                  onChange={(e) => handleNestedFieldChange('personalInfo', 'address', 'city', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.personalInfo.address.state}
                  onChange={(e) => handleNestedFieldChange('personalInfo', 'address', 'state', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.personalInfo.address.postalCode}
                  onChange={(e) => handleNestedFieldChange('personalInfo', 'address', 'postalCode', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 6: // Review & Submit
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Review Your Information</Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Please review all information carefully before submitting. Once submitted, your verification will be reviewed by our team.
            </Alert>

            {/* Progress Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Completion Status</Typography>
                  <Chip 
                    label={`${progress.completionPercentage}% Complete`}
                    color={progress.canSubmit ? 'success' : 'warning'}
                    icon={progress.canSubmit ? <CheckCircle /> : <Warning />}
                  />
                </Box>
                
                <LinearProgress 
                  variant="determinate" 
                  value={progress.completionPercentage}
                  sx={{ height: 8, borderRadius: 4, mb: 2 }}
                />

                {progress.missingFields.length > 0 && (
                  <Alert severity="warning">
                    <Typography variant="subtitle2" gutterBottom>Missing Information:</Typography>
                    <List dense>
                      {progress.missingFields.map((field, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Warning fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={field} />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmitVerification}
                disabled={!progress.canSubmit || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                sx={{ minWidth: 200 }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Info sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6">Section Under Development</Typography>
            <Typography variant="body2" color="text.secondary">
              This section is being built. Please navigate to other sections.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate(returnUrl)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Driver Verification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {verificationPurpose === 'driver_application' 
              ? 'Complete your driver application' 
              : 'Complete verification to unlock all features'}
          </Typography>
        </Box>
      </Box>

      {/* Rejection Notice */}
      {user?.id && (
        <RejectionNotice 
          userId={user.id} 
          onDismiss={() => verificationPersistence.clearRejectedFieldInfo(user.id)}
        />
      )}

      {/* Progress Indicator */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Verification Progress</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={`${progress.completionPercentage}% Complete`}
                color={progress.completionPercentage === 100 ? 'success' : 'primary'}
                size="small"
              />
              {isSaving && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="primary">Saving...</Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={progress.completionPercentage}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </CardContent>
      </Card>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={currentStep} alternativeLabel>
            {verificationSteps.map((step, index) => (
              <Step key={step.label} completed={progress.completedSections.includes(getStepKey(index))}>
                <StepLabel icon={step.icon}>
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <Card>
        <CardContent>
          {renderCurrentStepContent()}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
          disabled={currentStep === 0}
          startIcon={<ArrowBack />}
        >
          Previous
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => saveProgress(formData, true)}
            disabled={isSaving}
            startIcon={<Save />}
          >
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>
          
          <Button
            variant="contained"
            onClick={() => setCurrentStep(prev => Math.min(verificationSteps.length - 1, prev + 1))}
            disabled={currentStep === verificationSteps.length - 1}
            endIcon={<ArrowForward />}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Success Dialog */}
      <Dialog open={successDialog} onClose={() => setSuccessDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            Verification Submitted!
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Your verification has been successfully submitted and is now under review.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We'll notify you via email and in-app notification once the review is complete. 
            This typically takes 1-3 business days.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            onClick={() => {
              setSuccessDialog(false);
              navigate(returnUrl);
            }}
            startIcon={<CheckCircle />}
          >
            Return to Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper function to get step key for completion tracking
function getStepKey(stepIndex: number): string {
  const stepKeys = ['personalInfo', 'driverLicense', 'vehicleInfo', 'guarantors', 'backgroundCheck', 'bankingInfo', 'review'];
  return stepKeys[stepIndex] || '';
}